import { useGoogleLogin as useGoogleLoginCore, parseJwt } from '@myfunnow/web-core'
import { useThirdLogin } from '@/composables/useThirdLogin'
import { useLoginSdkInit } from '@/composables/useLoginSdkInit'
import * as loginService from '@/service/login'
import { ResponseCode } from '@/view-models/common'

interface GoogleJwtPayload {
  email: string
  name: string
}

export function useGoogleLogin(options?: { onSuccess?: () => void }) {
  const config = useRuntimeConfig()
  const nuxtApp = useNuxtApp()
  const { $axios, $store: store } = nuxtApp

  // Shared logic delegated to useThirdLogin
  const { showConsent, pendingUserInfo, onLoginSuccess, onNeedRegister, handleConsentConfirm, openErrorDialog, resetConsent } = useThirdLogin({
    provider: 'google',
    registerOptions: { byGoogle: true },
    onSuccess: options?.onSuccess,
  })

  // Google-specific SDK setup
  const {
    isReady: isGoogleReady,
    initialize,
    renderButton,
  } = useGoogleLoginCore({
    clientId: config.public.GOOGLE_OAUTH_CLIENT_ID,
    onCredentialResponse: response => {
      loginFunNow(response.credential)
    },
    idConfiguration: {
      color_scheme: 'light',
      cancel_on_tap_outside: false,
      auto_select: true,
    },
  })

  useLoginSdkInit(initialize)

  // Google-specific: call backend API, handle response
  async function loginFunNow(idToken: string) {
    try {
      const loginResult = await loginService.googleLogin($axios as any, { idToken })
      onLoginSuccess(loginResult.data.uid)
      await store.dispatch('authentication/getUserInfo')
      return true
    } catch (error: any) {
      const err = error as { code?: number; message?: string }
      if (err.code === ResponseCode.TokenNotFound) {
        preparePendingUserInfo(idToken)
        return false
      }
      openErrorDialog(err.message ?? '')
      return false
    }
  }

  // Google-specific: decode JWT to extract user info
  function preparePendingUserInfo(idToken: string) {
    try {
      const decoded = parseJwt<GoogleJwtPayload>(idToken)
      onNeedRegister({
        email: decoded.email,
        name: decoded.name,
        id_token: idToken,
      })
    } catch (error: any) {
      console.error('preparePendingUserInfo: error', error)
      openErrorDialog(error instanceof Error ? error.message : String(error))
    }
  }

  return {
    isGoogleReady,
    showConsent,
    pendingUserInfo,
    handleConsentConfirm,
    renderButton,
    resetConsent,
  }
}
